import React, { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { LogoBanner } from '../components/Navigation';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Trophy } from 'lucide-react';

export default function PaymentSuccess() {
  const { user, loading, checkAuth } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [message, setMessage] = useState('Verifying your payment...');
  const hasPolled = useRef(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
      return;
    }

    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('failed');
      setMessage('No payment session found');
      return;
    }

    if (!hasPolled.current) {
      hasPolled.current = true;
      pollPaymentStatus(sessionId);
    }
  }, [loading, user, navigate, searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('failed');
      setMessage('Payment verification timed out. Please contact support if you were charged.');
      return;
    }

    try {
      const response = await fetch(`${API}/payments/status/${sessionId}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();

      if (data.payment_status === 'paid') {
        setStatus('success');
        setMessage('Welcome to The Bundle Golf Club! Your membership is now active.');
        if (checkAuth) {
          await checkAuth();
        }
        return;
      } else if (data.status === 'expired') {
        setStatus('failed');
        setMessage('Payment session expired. Please try again.');
        return;
      }

      setMessage('Confirming your payment...');
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setStatus('failed');
        setMessage('Error verifying payment. Please contact support.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-bundle-cream">
      {/* Navigation */}
      <nav className="bg-white border-b border-bundle-cream-dark py-4 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <LogoBanner size="small" />
        </div>
      </nav>

      <div className="max-w-md mx-auto px-6 py-16">
        <Card className="bg-white border-bundle-cream-dark overflow-hidden">
          {status === 'success' && (
            <div className="bg-gradient-bundle p-6 text-center">
              <Trophy className="w-12 h-12 text-bundle-gold mx-auto mb-2" />
              <h2 className="font-heading text-xl font-bold text-white">Welcome to the Club!</h2>
            </div>
          )}
          
          <CardContent className="p-8 text-center">
            {status === 'checking' && (
              <>
                <Loader2 className="w-16 h-16 text-bundle-green mx-auto mb-6 animate-spin" />
                <h1 className="font-heading text-2xl font-bold text-bundle-green mb-2">Processing Payment</h1>
                <p className="font-body text-gray-600 mb-6">{message}</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-20 h-20 bg-bundle-green/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-bundle-green" />
                </div>
                <h1 className="font-heading text-2xl font-bold text-bundle-green mb-2">Payment Successful!</h1>
                <p className="font-body text-gray-600 mb-6">{message}</p>
                <Button
                  data-testid="go-to-dashboard-btn"
                  onClick={() => navigate('/dashboard')}
                  className="w-full btn-bundle"
                >
                  Go to Dashboard
                </Button>
              </>
            )}

            {status === 'failed' && (
              <>
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-12 h-12 text-red-500" />
                </div>
                <h1 className="font-heading text-2xl font-bold text-bundle-green mb-2">Payment Issue</h1>
                <p className="font-body text-gray-600 mb-6">{message}</p>
                <div className="space-y-3">
                  <Button
                    onClick={() => navigate('/membership')}
                    className="w-full btn-bundle"
                  >
                    Try Again
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/dashboard')}
                    className="w-full border-bundle-green text-bundle-green hover:bg-bundle-green/5"
                  >
                    Back to Dashboard
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
